"use client";

import { c as _c } from "react/compiler-runtime";
import { PropsWithClassName } from "@/types/ui";
import { useFeatureMenu } from "@hooks/use-feature-menu";
import { useTranslations } from "next-intl";
import { AppSidebarGroup } from "@components/app/app-sidebar/app-sidebar-group";
import { useAppSelector } from "@lib/redux/hooks";
import { selectSessionUser } from "@features/application/app-selectors";
import { selectCurrentEvent } from "@features/events/events-selectors";
import { eventsTools } from "@features/events/events-tools";
import { selectCurrentWorkspace } from "@features/workspaces/workspaces-selectors";

/**
 * NavEvent is a functional React component that renders a navigation sidebar group for events.
 *
 * It leverages various hooks and tools to dynamically generate menu items based on the
 * current event and workspace context, making it adaptable to the application's state.
 *
 * Key functionalities include:
 * - Utilizing translations for localization.
 * - Dynamically building sidebar menu items specific to a workspace and event.
 * - Rendering a sidebar group component with the generated menu items.
 *
 * If there is no current workspace or event, the component renders nothing.
 *
 * @param {PropsWithClassName} props - The props for the component which include the `className`.
 * @returns A rendered sidebar group component or null if workspace or event is absent.
 */
export const NavEvent = t0 => {
  const $ = _c(12);
  const {
    className,
    isPrivateMode
  } = t0;
  const t = useTranslations("features.events.ui.nav-events");
  const workspace = useAppSelector(selectCurrentWorkspace);
  const event = useAppSelector(selectCurrentEvent);
  const user = useAppSelector(selectSessionUser);
  let t1;
  if ($[0] !== event || $[1] !== isPrivateMode || $[2] !== user || $[3] !== workspace) {
    t1 = (localeAs, pageInfo) => eventsTools.buildSidebarMenuItems(localeAs, event, workspace, pageInfo, user, false, isPrivateMode);
    $[0] = event;
    $[1] = isPrivateMode;
    $[2] = user;
    $[3] = workspace;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const menuItems = useFeatureMenu(t1);
  if (!workspace || !event) {
    return null;
  }
  const t2 = event?.hint ?? "";
  let t3;
  if ($[5] !== t || $[6] !== t2) {
    t3 = t("title", {
      name: t2
    });
    $[5] = t;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== className || $[9] !== menuItems || $[10] !== t3) {
    t4 = <AppSidebarGroup title={t3} items={menuItems} className={className} />;
    $[8] = className;
    $[9] = menuItems;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};